import Editor, { EditorProps, OnMount } from '@monaco-editor/react';
import { ReactNode, useEffect, useState } from 'react';
import { format } from 'sql-formatter';
import { twMerge } from 'tailwind-merge';
import { InputLabel, Spinner, HelperText } from '@scale/scaleui';

// These are locks to prevent registering the same language twice
const sqlRegistered = false;
// const registeredSuggestions: {
//   language: string;
//   suggestions: string[];
// }[] = [];

export const DEFAULT_CODE_PROPS = {
  className: '',
  required: false,
};

type IStandaloneCodeEditor = Parameters<OnMount>[0];

export type CodeEditorFieldProps = {
  disabled?: boolean;
  required?: boolean;
  label?: string;
  labelDescription?: ReactNode;
  error?: boolean;
  value?: string;
  name?: string;
  autoFocus?: boolean;
  // suggestions?: string[];
  className?: string;
  helperText?: ReactNode;
  editorProps?: Omit<EditorProps, 'options'>;
  editorOptions?: EditorProps['options'];
  tooltipContentClassName?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onChange?: (value: any) => void;
  onKeyDown?: (e: any) => void;
};

export const CodeEditorField = ({
  className,
  label,
  labelDescription,
  required,
  disabled,
  error,
  value,
  name,
  helperText,
  // suggestions = [],
  autoFocus,
  onFocus,
  onBlur,
  onChange,
  onKeyDown,
  editorOptions = {
    fontSize: 14,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    padding: {
      top: 16,
      bottom: 16,
    },
    formatOnPaste: true,
    formatOnType: true,
    renderLineHighlight: 'none',
  },
  editorProps = {},
  tooltipContentClassName,
}: CodeEditorFieldProps) => {
  const [editor, setEditor] = useState<IStandaloneCodeEditor | null>(null);

  const handleEditorDidMount: OnMount = (editor, monaco) => {
    setEditor(editor);
    // TODO: @kevinli The suggestions API need a range as well!
    // if (
    //   editorProps.language &&
    //   !registeredSuggestions.find(
    //     s => s.language === editorProps.language && s.suggestions === suggestions,
    //   )
    // ) {
    //   monaco.languages.registerCompletionItemProvider(editorProps.language, {
    //     provideCompletionItems: () => ({
    //       suggestions: suggestions.map(suggestion => ({
    //         label: suggestion,
    //         kind: monaco.languages.CompletionItemKind.Keyword,
    //         insertText: suggestion,
    //       })),
    //     }),
    //   });
    //   registeredSuggestions.push({
    //     language: editorProps.language,
    //     suggestions,
    //   });
    // }

    if (!sqlRegistered) {
      // define a document formatting provider
      // then you contextmenu will add an "Format Document" action
      monaco.languages.registerDocumentFormattingEditProvider('sql', {
        provideDocumentFormattingEdits(model: any) {
          return [
            {
              range: model.getFullModelRange(),
              text: format(model.getValue()),
            },
          ];
        },
      });

      // define a range formatting provider
      // select some codes and right click those codes
      // you contextmenu will have an "Format Selection" action
      monaco.languages.registerDocumentRangeFormattingEditProvider('sql', {
        provideDocumentRangeFormattingEdits(model: any, range: any) {
          return [
            {
              range,
              text: format(model.getValueInRange(range)),
            },
          ];
        },
      });
    }

    if (autoFocus) {
      editor.focus();
    }
  };

  useEffect(() => {
    if (editor) {
      const formatDocument = () => {
        editor?.getAction('editor.action.formatDocument')?.run();
      };
      // on first initialization
      editor.onDidChangeModelLanguageConfiguration(formatDocument);
      // on every initialization
      editor.onDidLayoutChange(formatDocument);

      editor.onDidFocusEditorWidget(() => {
        onFocus?.();
      });
      editor.onDidBlurEditorWidget(() => {
        formatDocument();
        onBlur?.();
      });
      editor.onKeyDown((e: any) => {
        onKeyDown?.(e);
      });
    }
  }, [editor, onFocus, onBlur, onKeyDown]);

  return (
    <div className={twMerge('scaleui flex flex-col z-20', className)}>
      <InputLabel
        required={required}
        error={error}
        disabled={disabled}
        labelDescription={labelDescription}
        tooltipContentClassName={tooltipContentClassName}
      >
        {label}
      </InputLabel>
      <Editor
        {...editorProps}
        loading={
          <div
            className={twMerge(
              'h-full w-full flex items-center justify-center',
              editorProps.className,
            )}
          >
            <Spinner size="md" variant={'primary'} />
          </div>
        }
        wrapperProps={{
          className: 'shadow-sm overflow-hidden rounded-b-lg',
          name,
        }}
        options={editorOptions}
        value={value}
        onChange={onChange}
        onMount={handleEditorDidMount}
      />
      {helperText ? (
        <HelperText error={error} className="px-1 w-full whitespace-pre-line">
          {helperText}
        </HelperText>
      ) : null}
    </div>
  );
};
